import type { BallotCandidate } from "@/lib/stimmzettel/BallotFactory/BallotCandidate";

export type BallotPageSizeNames = "A4_2" | "A4" | "2_A4" | "A3_2" | "A3_3" | "A5" | "4_A4" | "4_A4L";

export const BallotPageSizeDescriptions: { [k in BallotPageSizeNames]: string } = {
	A4_2: "1 Stimmzettel auf A4 in 2 Spalten",
	A5: "1 Stimmzettel auf A5",
	A4: "1 Stimmzettel auf A4",
	"2_A4": "2 Stimmzettel auf A4",
	"4_A4L": "4 Stimmzettel auf A4",
	"4_A4": "4 Stimmzettel auf A4 Hochkant",
	A3_2: "1 Stimmzettel auf A3 in 2 Spalten",
	A3_3: "1 Stimmzettel auf A3 in 3 Spalten"
};

export interface BallotVoteCommons {
	what: string;
	index: number;
	prefilled?: { [candidateName: string]: number };
}

export interface BallotVoteGO8 extends BallotVoteCommons {
	type: "go8";
}

export interface BallotVoteEW19 extends BallotVoteCommons {
	type: "ew19";
	candidates: BallotCandidate[];
}

export interface BallotVoteVEW20 extends BallotVoteCommons {
	type: "vew20";
	voteCount: number;
	candidates: BallotCandidate[];
}

export interface BallotVoteYN extends BallotVoteCommons {
	type: "yn";
	candidates: BallotCandidate[];
}

export interface BallotVoteSW23 extends BallotVoteCommons {
	type: "sw23";
	maxPoints: number;
	candidates: BallotCandidate[];
}

export interface BallotVoteDL38 extends BallotVoteCommons {
	type: "dl38";
	candidates: BallotCandidate[];
}

export function hasVoteCount(vote: BallotVote): vote is BallotVoteVEW20 {
	return vote.type === "vew20";
}

export function hasSimpleCandidate(vote: BallotVote): vote is BallotVoteEW19 | BallotVoteVEW20 | BallotVoteYN | BallotVoteDL38 {
	return vote.type === "ew19" || vote.type === "yn" || vote.type === "vew20" || vote.type === "dl38";
}

export function hasMaxPoints(vote: BallotVote): vote is BallotVoteSW23 {
	return vote.type === "sw23";
}

export function hasCandidateWithMinSpot(vote: BallotVote): vote is BallotVoteSW23 {
	return vote.type === "sw23";
}

export function renderBallotCandidateName(candidate: BallotCandidate): string {
	const output = [];

	if (candidate.title) {
		output.push(candidate.title);
	}

	output.push(candidate.firstName);
	output.push(candidate.lastName);

	if (candidate.extra) {
		output.push(candidate.extra);
	}

	return output.join(" ");
}

export type BallotVote = BallotVoteEW19 | BallotVoteGO8 |
	BallotVoteYN | BallotVoteSW23 | BallotVoteVEW20 | BallotVoteDL38;

export const BallotVoteDescriptions: { [k in BallotVote["type"]]: string } = {
	go8: "Abstimmung nach § 8 der Geschäftsordnung",
	ew19: "Einzelwahl nach § 19 der Allgemeinen Wahlordnung",
	yn: "Abstimmung über Bewerber*innen mit Ja/Nein",
	sw23: "Verfahren für staatliche Wahlen nach § 23 der Allgemeinen Wahlordnung",
	vew20: "Verbundene Einzelwahl nach § 20 der Allgemeinen Wahlordnung",
	dl38: "Verfahren zur Wahl der Delegierten nach § 38 der Allgemeinen Wahlordnung"
};

export interface Ballot {
	secret: string;
	eventName: string;
	zkLead: string;
	zkMembers: string[];
	location: string;
	paperSize: BallotPageSizeNames;
	votes: BallotVote[];
}
